import { IFaireMoney } from '@faire/web-api--source/indigofair/data/IFaireMoney';
import { IPaymentTermData } from '@faire/web-api--source/indigofair/data/IPaymentTermData';
import { PaymentTerm } from '@faire/web-api--source/indigofair/data/PaymentTerm';

export namespace IRetailerCreditSummary {
  export enum LimitIncreaseStatus {
    NOT_ELIGIBLE = 'NOT_ELIGIBLE',
    ELIGIBLE = 'ELIGIBLE',
    ON_REVIEW = 'ON_REVIEW',
  }

  export namespace ILimitIncrease {
    /**
     * Return an instance of ILimitIncrease with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (partial?: Partial<ILimitIncrease>): ILimitIncrease =>
      ({
        next_available_terms_limits: [],
        ...(partial ?? {}),
      }) as ILimitIncrease;
  }

  export interface ILimitIncrease {
    status: keyof typeof IRetailerCreditSummary.LimitIncreaseStatus | undefined;
    on_review_limit_cents: number | undefined;
    next_terms_limit: IFaireMoney | undefined;
    last_application_at: number | undefined;
    next_application_allowed_at: number | undefined;
    next_available_terms_limits: Array<IFaireMoney>;
  }

  /**
   * Return an instance of IRetailerCreditSummary with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (
    partial?: Partial<IRetailerCreditSummary>
  ): IRetailerCreditSummary =>
    ({
      ...(partial ?? {}),
    }) as IRetailerCreditSummary;
}

export interface IRetailerCreditSummary {
  /**
   * Total credit we extended to this Retailer for ordering with terms (including installments).
   * @deprecated
   */
  total_credit_cents: number | undefined;
  /**
   * Current available credit this Retailer has to order with terms (including installments).
   * @deprecated
   */
  available_credit_cents: number | undefined;
  /**
   * Current reward credits available in this Retailer's balance.
   * @deprecated
   */
  available_reward_credits_cents: number | undefined;
  /**
   * Total past due amount for this Retailer, including failed PAYMENT_ON_SHIPMENT orders.
   * @deprecated
   */
  total_past_due_amount_cents: number | undefined;
  /**
   * Grace amount that a retailer can go over their terms.
   * @deprecated
   */
  terms_grace_cents: number | undefined;
  limit_increase: IRetailerCreditSummary.ILimitIncrease | undefined;
  /**
   * This is the available credits summing the grace. Please don't do availableCreditCents + termsGraceCents
   * because these values are capped at 0.
   * @deprecated
   */
  available_credit_cents_with_grace: number | undefined;
  /**
   * @deprecated
   */
  maximum_available_payment_term: keyof typeof PaymentTerm | undefined;
  /**
   * Use maximumAvailablePaymentTermData instead
   * @deprecated
   */
  maximum_available_payment_term_description: string | undefined;
  /**
   * Use maximumAvailablePaymentTermData instead
   * @deprecated
   */
  is_net_term: boolean | undefined;
  /**
   * This is the sum of all the brand discounts the retailer has available (for any brands)
   */
  total_brand_discount_credit: IFaireMoney | undefined;
  maximum_available_payment_term_data: IPaymentTermData | undefined;
  /**
   * This is the sum of availableRewardCreditsCents and totalBrandDiscountCredit
   */
  available_reward_credits_and_brand_discounts: IFaireMoney | undefined;
  previous_terms_limit: IFaireMoney | undefined;
  /**
   * Total credit we extended to this Retailer for ordering with terms (including installments).
   */
  total_credit: IFaireMoney | undefined;
  /**
   * Current available credit this Retailer has to order with terms (including installments).
   */
  available_credit: IFaireMoney | undefined;
  /**
   * Current reward credits available in this Retailer's balance.
   */
  available_reward_credits: IFaireMoney | undefined;
  /**
   * Total past due amount for this Retailer, including failed PAYMENT_ON_SHIPMENT orders.
   */
  total_past_due_amount: IFaireMoney | undefined;
  /**
   * Grace amount that a retailer can go over their terms.
   */
  terms_grace: IFaireMoney | undefined;
  /**
   * This is the available credits summing the grace. Please don't do availableCreditCents + termsGraceCents
   * because these values are capped at 0.
   */
  available_credit_with_grace: IFaireMoney | undefined;
  /**
   * Unused
   * @deprecated
   */
  insider_cashback_credit: IFaireMoney | undefined;
  limit_updated_at: number | undefined;
}
